
import { TOKEN_MAP } from '@/src/constants';
import { useAuth, useLanguage, useNetwork } from '@/src/contexts';
import { getNetworkConfig, getText, normalizeCurrencyCode, TranslationKey } from '@/src/helpers';
import { useAccountInfo, useAccountLines } from '@/src/hooks';
import { useFireblocksVaultAccount, useFireblocksVaultAccountById } from '@/src/hooks/apis/fireblocks';
import { useTokenizedAssetPools } from '@/src/hooks/apis/tokenized-asset-pool';
import { Token } from '@/src/models';
import { Page } from '@/src/ui-components';
import { TokenIcon } from '@/src/ui-components/token-icon';
import { Button } from '@/src/ui-components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/src/ui-components/ui/dialog';
import { ArrowUpDown, Copy } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { dropsToXrp } from 'xrpl';

interface Balance {
  token: Token | null; // Token from TOKEN_MAP if available
  assetId: string | null; // Fireblocks asset ID (can be null if not mapped)
  assetName: string; // Display name for the asset
  balance: string; // Balance from XRPL
  issuer: string | null; // XRPL issuer address (null for XRP)
  currencyCode: string; // XRPL currency code
  no_ripple: boolean; // From XRPL account_lines
  no_ripple_peer: boolean; // From XRPL account_lines
}

interface AccountLine {
  currency: string;
  account: string;
  balance: string;
  no_ripple: boolean;
  no_ripple_peer: boolean;
}

function AccountContent() {
  const { language } = useLanguage();
  const { network } = useNetwork();
  const { user } = useAuth();

  // Use Fireblocks authentication instead of wallet connection
  const { data: vaultAccount } = useFireblocksVaultAccount();
  const { data: vaultAccountWithAssets, isLoading: isLoadingVaultAssets } = useFireblocksVaultAccountById(user?.vault_id || undefined);
  const { data: tokenizedAssetPools, isLoading: isLoadingAssetPools } = useTokenizedAssetPools();

  // Get XRPL address directly from auth context (no API calls needed)
  const xrplAddress = user?.xrp_address;
  const account = xrplAddress;

  const [balances, setBalances] = useState<Balance[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddTokenModalOpen, setIsAddTokenModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getTranslation = (key: TranslationKey) => getText(language, key);
  const tokens = TOKEN_MAP[network];

  const { data: accountInfo } = useAccountInfo({
    account: account ?? '',
  });

  const { data: accountLines } = useAccountLines({
    account: account ?? '',
  });

  const fetchBalances = useCallback(async () => {
    if (!account || !network.startsWith('xrpl')) {
      setLoading(false);
      return;
    }

    // Wait for asset pools to load before processing balances
    if (isLoadingAssetPools) {
      console.log('[Wallet] Waiting for asset pools to load...');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const networkConfig = getNetworkConfig(network);
      if (!networkConfig || !networkConfig.wsUrl) {
        throw new Error('Invalid network configuration');
      }

      const balancesArray: Balance[] = [];

      // Create a map from (currency_code + issuer) to tokenized asset pool
      // This allows us to look up Fireblocks asset ID from XRPL data
      const assetPoolMap = new Map(
        tokenizedAssetPools?.objects?.map(pool => [
          `${pool.token_currency_code}:${pool.xrpl_issuer_address}`,
          pool
        ]) || []
      );

      console.log('[Wallet] Asset pool map:', Array.from(assetPoolMap.entries()));

      // 1. Add XRP balance from AccountInfo (native currency)
      const xrpToken = tokens.find((t) => t.code === 'XRP');
      if (accountInfo?.account_data?.Balance) {
        const xrpBalance = String(dropsToXrp(accountInfo.account_data.Balance));

        // Find XRP or XRP_TEST asset in Fireblocks vault for asset ID
        const xrpVaultAsset = vaultAccountWithAssets?.assets?.find(asset =>
          asset.id === 'XRP' || asset.id === 'XRP_TEST'
        );

        balancesArray.push({
          token: xrpToken || null,
          assetId: xrpVaultAsset?.id || null,
          assetName: 'XRP',
          balance: xrpBalance,
          issuer: null, // XRP has no issuer
          currencyCode: 'XRP',
          no_ripple: false, // XRP doesn't have these flags
          no_ripple_peer: false,
        });
      }

      // 2. Add token balances from AccountLines (issued currencies)
      if (accountLines?.lines) {
        (accountLines.lines as AccountLine[]).forEach((line: AccountLine) => {
          const currencyCode = line.currency;
          const issuerAddress = line.account.trim();

          // Look up in asset pool map to get Fireblocks asset ID
          const lookupKey = `${currencyCode}:${issuerAddress}`;
          const assetPool = assetPoolMap.get(lookupKey);

          // Try to find matching token in TOKEN_MAP
          const token = tokens.find((t) =>
            t.code === currencyCode && t.issuer === issuerAddress
          );

          const assetName = token?.description || normalizeCurrencyCode(currencyCode) || currencyCode;

          console.log('[Wallet] Processing trust line:', {
            currencyCode,
            issuerAddress,
            assetName,
            fireblocksAssetId: assetPool?.fireblocks_asset_id || 'not mapped',
            foundInTokenMap: !!token
          });

          balancesArray.push({
            token: token || null,
            assetId: assetPool?.fireblocks_asset_id || null,
            assetName: assetName,
            balance: line.balance,
            issuer: issuerAddress,
            currencyCode: currencyCode,
            no_ripple: line.no_ripple,
            no_ripple_peer: line.no_ripple_peer,
          });
        });
      }

      setBalances(balancesArray);
    } catch (error) {
      console.error('Error fetching balances:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch balances');
      setBalances([]);
    } finally {
      setLoading(false);
    }
  }, [account, network, tokens, accountInfo, accountLines, vaultAccountWithAssets, tokenizedAssetPools, isLoadingAssetPools]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  // Show component if we have Fireblocks authentication and XRPL address
  if (!vaultAccount?.id || !xrplAddress || !network.startsWith('xrpl')) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-muted-foreground">
          Please configure your Fireblocks account to view your portfolio
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg border p-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-medium">{getTranslation('walletAddress')}:</h2>
            <a
              href={`https://livenet.xrpl.org/accounts/${account}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-sm text-primary hover:underline"
            >
              {account}
            </a>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                if (account) {
                  navigator.clipboard.writeText(account);
                  toast.success('Address copied to clipboard');
                }
              }}
            >
              <Copy size={16} strokeWidth={1.5} absoluteStrokeWidth={true} />
            </Button>
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsAddTokenModalOpen(true)}
          >
            <ArrowUpDown size={16} strokeWidth={1.5} absoluteStrokeWidth={true} />
          </Button>
        </div>
      </div>

      <div className="rounded-lg border p-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-lg font-medium">Email Address:</span>
            <span className="text-lg text-muted-foreground">
              {vaultAccount?.name ?? 'Email not found'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-medium">Vault Account ID:</span>
            <span className="text-lg text-muted-foreground">
              {vaultAccount?.id ?? 'Vault account not found'}
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-lg border">
        {loading || isLoadingVaultAssets || isLoadingAssetPools ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-8 w-8 rounded-full bg-muted"></div>
                  <div className="flex-1">
                    <div className="h-4 w-24 rounded bg-muted"></div>
                  </div>
                  <div className="h-4 w-20 rounded bg-muted"></div>
                </div>
              ))}
            </div>
          </div>
        ) : balances.length === 0 ? (
          <div className="flex h-32 items-center justify-center">
            <p className="text-muted-foreground">No token balances found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-3 text-left text-base font-semibold">Token</th>
                  <th className="p-3 text-left text-base font-semibold">Issuer</th>
                  <th className="p-3 text-left text-base font-semibold">Flags</th>
                  <th className="p-3 text-right text-base font-semibold">Balance</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {balances.map((balance, index) => (
                  <tr key={index} className="border-b">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <TokenIcon
                          src={balance.token?.icon || '/placeholder-token.svg'}
                          alt={balance.assetName}
                          width={32}
                          height={32}
                          className="h-8 w-8 object-contain"
                        />
                        <div>
                          <div className="text-sm font-medium">
                            {balance.assetName}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {balance.assetId || 'n/a'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      {balance.issuer ? (
                        <span className="font-mono text-xs text-muted-foreground">
                          {balance.issuer}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2 text-xs">
                        <span className={`px-2 py-1 rounded font-medium ${balance.no_ripple ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                          no_ripple
                        </span>
                        <span className={`px-2 py-1 rounded font-medium ${balance.no_ripple_peer ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                          no_ripple_peer
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-right text-sm font-medium font-mono">
                      {Number(balance.balance).toFixed(2)}
                    </td>
                    <td className="p-3">
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={isAddTokenModalOpen} onOpenChange={setIsAddTokenModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deposit / Withdrawal</DialogTitle>
            <DialogDescription>Please Contact Support for Deposit / Withdrawal Instructions</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AccountPage() {
  return (
    <Page>
      <Page.Body className="bg-card">
        <AccountContent />
      </Page.Body>
    </Page>
  );
}
